/*
 * Credentialing Field Coverage — one-glance view of:
 *   - Canonical broker-safe fields (from backend credentialing_spec.py)
 *   - Fields Kaizen actually asks for (scraped via /api/tpt/kaizen-credentials/schema/capture)
 *   - Fields MedexOVA currently collects
 * Highlights gaps in amber. Lets you trigger a fresh Kaizen schema scrape.
 */
import React, { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { apiClient } from "../../lib/api";
import { RefreshCw, CheckCircle2, AlertTriangle, Download, ShieldCheck } from "lucide-react";

const TP = "/tpt";

function Pill({ label, on, tone = "green" }) {
  const toneCls = on
    ? (tone === "green" ? "bg-green-100 text-green-800" : tone === "blue" ? "bg-blue-100 text-blue-800" : "bg-slate-100 text-slate-700")
    : "bg-slate-100 text-slate-400 line-through";
  return <span className={`inline-block text-[9px] font-semibold px-1.5 py-0.5 rounded ${toneCls}`}>{label}</span>;
}

export default function CredentialingCoverage() {
  const [kind, setKind] = useState("tp");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [scraping, setScraping] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await apiClient.get(`${TP}/kaizen-credentials/coverage`, { params: { kind } });
      setData(data);
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed to load coverage"); }
    finally { setLoading(false); }
  }, [kind]);

  useEffect(() => { load(); }, [load]);

  const scrapeSchema = async () => {
    if (!window.confirm("Trigger a Kaizen schema capture? This logs into the Kaizen portal and scrapes the Add-TP + Add-Driver form pages. Takes ~30 seconds.")) return;
    setScraping(true);
    try {
      const { data } = await apiClient.post(`${TP}/kaizen-credentials/schema/capture`);
      if (data.ok) {
        toast.success(`Captured ${data.tp?.count || 0} TP fields · ${data.driver?.count || 0} driver fields`);
        load();
      } else {
        toast.error(data.error || "Schema capture failed");
      }
    } catch (e) { toast.error(e?.response?.data?.detail || "Schema capture failed"); }
    finally { setScraping(false); }
  };

  // Group rows by section
  const sections = {};
  (data?.rows || []).forEach(r => {
    const s = r.section || "other";
    (sections[s] = sections[s] || []).push(r);
  });

  return (
    <div data-testid="credentialing-coverage-panel">
      <div className="flex items-center gap-3 mb-4">
        <ShieldCheck className="w-8 h-8 text-forest"/>
        <div>
          <h2 className="text-xl font-bold text-slate_navy">Credentialing Field Coverage</h2>
          <p className="text-sm text-slate-500">Canonical broker-safe fields × Kaizen&apos;s live schema × MedexOVA&apos;s collected fields. Amber rows are gaps.</p>
        </div>
      </div>

      <div className="flex items-center gap-2 mb-4 flex-wrap">
        {[{k:"tp",l:"Transportation Provider"},{k:"driver",l:"Driver"}].map(o => (
          <button key={o.k} onClick={() => setKind(o.k)}
            className={`text-sm px-3 py-1.5 rounded-md ${kind === o.k ? "bg-forest text-white" : "border border-[#E2E0D8]"}`}
            data-testid={`coverage-kind-${o.k}`}>
            {o.l}
          </button>
        ))}
        <div className="flex-1"/>
        <button onClick={load} disabled={loading} className="inline-flex items-center gap-1 border border-[#E2E0D8] px-3 py-1.5 rounded-md text-xs" data-testid="coverage-refresh">
          <RefreshCw size={12} className={loading ? "animate-spin" : ""}/> Refresh
        </button>
        <button onClick={scrapeSchema} disabled={scraping} className="inline-flex items-center gap-1 bg-forest text-white px-3 py-1.5 rounded-md text-xs font-semibold disabled:opacity-60" data-testid="coverage-scrape-schema">
          <Download size={12}/> {scraping ? "Scraping…" : "Scrape Kaizen schema"}
        </button>
      </div>

      {data && (
        <div className="bg-white border border-[#E2E0D8] rounded-xl p-4 mb-4">
          <div className="flex flex-wrap items-center gap-4">
            <div>
              <div className="text-[10px] uppercase tracking-widest text-slate-500">Coverage</div>
              <div className="text-3xl font-black text-forest">{data.coverage_pct}%</div>
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-widest text-slate-500">Fields covered</div>
              <div className="text-lg font-bold">{data.covered} / {data.total_fields}</div>
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-widest text-slate-500">Gaps</div>
              <div className={`text-lg font-bold ${data.gap_count === 0 ? "text-green-700" : "text-amber-700"}`}>{data.gap_count}</div>
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-widest text-slate-500">Kaizen schema captured</div>
              <div className="text-xs text-slate-600">{data.kaizen_captured_at ? new Date(data.kaizen_captured_at).toLocaleString() : "— (never)"}</div>
            </div>
          </div>
        </div>
      )}

      {Object.entries(sections).map(([section, rows]) => (
        <div key={section} className="bg-white border border-[#E2E0D8] rounded-xl mb-4 overflow-hidden">
          <div className="bg-slate-50 px-4 py-2 text-[10px] uppercase tracking-widest text-slate-600 font-semibold">
            {section.replace(/_/g, " ")}
          </div>
          <table className="w-full text-xs">
            <thead className="text-slate-500 uppercase tracking-widest text-[10px]">
              <tr>
                <th className="text-left px-3 py-1.5">Field</th>
                <th className="text-left px-3 py-1.5">Type</th>
                <th className="text-center px-2 py-1.5">Canonical</th>
                <th className="text-center px-2 py-1.5">Kaizen</th>
                <th className="text-center px-2 py-1.5">MedexOVA</th>
                <th className="text-center px-2 py-1.5">Status</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-[#F1EFE8]">
              {rows.map(r => (
                <tr key={r.key} className={r.gap ? "bg-amber-50" : ""} data-testid={`coverage-row-${r.key}`}>
                  <td className="px-3 py-1.5">
                    <div className="font-medium text-slate-900">{r.label}</div>
                    {r.hint && <div className="text-[10px] text-slate-500">{r.hint}</div>}
                    <div className="text-[9px] font-mono text-slate-400">{r.key}</div>
                  </td>
                  <td className="px-3 py-1.5 text-slate-500">{r.type}{r.required ? " *" : ""}</td>
                  <td className="px-2 py-1.5 text-center"><Pill label="canonical" on={r.canonical}/></td>
                  <td className="px-2 py-1.5 text-center"><Pill label="kaizen" on={r.kaizen} tone="blue"/></td>
                  <td className="px-2 py-1.5 text-center"><Pill label="collected" on={r.a_stat}/></td>
                  <td className="px-2 py-1.5 text-center">
                    {r.gap
                      ? <span className="inline-flex items-center gap-1 text-amber-800 text-[10px] font-semibold"><AlertTriangle size={10}/> GAP</span>
                      : <CheckCircle2 size={12} className="text-green-600 inline"/>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ))}

      {!loading && !data && (
        <div className="border border-dashed border-[#E2E0D8] rounded-xl p-8 text-center text-sm text-slate-500">
          Coverage data unavailable. Try Refresh, then Scrape Kaizen schema for a live comparison.
        </div>
      )}
    </div>
  );
}
